import { Component, inject, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ScoButtonModule,
  ScoToastNotificationService,
  ScoToastNotificationOptions,
} from '@schueco/schueco-ui-components';

import { Store } from '@ngrx/store';
import { selectIsDownloadDocumentActive, selectDownloadDocumentFilename } from '@frontend/data-access-general';
import { combineLatest, Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'dc-toast-notification',
  standalone: true,
  imports: [ReactiveFormsModule, ScoButtonModule],
  template: ``,
})
export class ToastNotificationComponent implements OnDestroy {
  private store = inject(Store);
  private translate = inject(TranslateService);

  private readonly toasts = inject(ScoToastNotificationService);
  private subscriptions: Subscription[] = [];
  private downloadStartTime = 0;
  private currentFilename = '';
  private downloadInProgress = false;
  private lastDownloadState = { isActive: false, filename: '' };
  private initialNotificationShown = false;

  message = '';
  options: ScoToastNotificationOptions = {
    severity: 'info',
    maxWidth: 500,
    dismiss: 'auto',
    duration: 5000,
  };

  constructor() {
    const downloadStateSub = combineLatest([
      this.store.select(selectIsDownloadDocumentActive),
      this.store.select(selectDownloadDocumentFilename),
    ]).subscribe(([isActive, filename]) => {
      // Check if the state has actually changed
      const stateChanged = isActive !== this.lastDownloadState.isActive || filename !== this.lastDownloadState.filename;

      // Remember the current state
      this.lastDownloadState = { isActive, filename };

      // Download has started - this detects when a new download is initiated
      if (isActive && !this.downloadInProgress) {
        this.downloadStartTime = Date.now();
        this.downloadInProgress = true;
        this.initialNotificationShown = false;
        this.currentFilename = '';

        // Only show initial notification if we already have a filename
        if (filename) {
          this.currentFilename = filename;
          this.initialNotificationShown = true;
          this.notifyWithFilename(filename);
        }
      }
      // Detect when a new download starts while a previous one is still active
      else if (
        isActive &&
        this.downloadInProgress &&
        stateChanged &&
        this.lastDownloadState.isActive &&
        (!this.lastDownloadState.filename || this.lastDownloadState.filename !== filename)
      ) {
        if (filename) {
          this.currentFilename = filename;
          this.notifyWithFilename(filename);
        }
      }
      // Handle filename changes during an active download
      else if (isActive && this.downloadInProgress && filename && stateChanged) {
        console.log('Filename changed during download, updating notification with new filename');

        // Always show notification when filename changes
        if (filename !== this.currentFilename) {
          this.currentFilename = filename;
          this.notifyWithFilename(filename);
          this.initialNotificationShown = true;
        }
      }

      // Download has ended
      else if (!isActive && this.downloadInProgress) {
        this.downloadInProgress = false;
      }
    });

    this.subscriptions.push(downloadStateSub);
  }

  notifyWithFilename(filename: string) {
    if (!filename) {
      return;
    }
    const fileMessage = `${this.translate.instant(`NOTIFICATION_TOAST`)}`;
    this.toasts.notify(fileMessage, this.options);
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
